/**
 * Bank top-up — the dealer reports a bank transfer and uploads the slip.
 *
 * Layout follows the receipt-upload flows researched in Mobbin (Navan's
 * "Upload receipt(s)" dropzone and Wise's detail form): the pay-in details stay
 * visible next to the form so the dealer never has to memorise an account
 * number, and the dropzone sits under the amount it is proving.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@evocha/api-client";
import {
  Banknote,
  CheckCircle2,
  Copy,
  Eye,
  FileText,
  ImageUp,
  Landmark,
  Loader2,
  X,
} from "lucide-react";
import { dealerApi } from "../lib/api";
import { dateTime, toMinor, tzs, tzsPlain } from "../lib/format";
import { fetchEvidenceUrl, releaseEvidenceUrl, submitSlip } from "../lib/evidence";
import { PageHead } from "../components/PageHead";

interface BankAccount {
  id: string;
  bank: string;
  account_name?: string;
  account_number?: string;
  branch?: string;
  teller?: string;
  note?: string;
}

interface AccountsResponse {
  accounts: BankAccount[];
  min_amount_minor: number;
  max_amount_minor: number;
  slip_max_bytes: number;
  retention_days: number;
  message: string | null;
}

interface TopupRequest {
  id: string;
  reference: string;
  amount_minor: number;
  credited_amount_minor: number | null;
  state: string;
  account_id: string | null;
  bank_reference: string | null;
  paid_at: string | null;
  note: string | null;
  file_mime: string;
  original_name: string | null;
  has_slip: boolean;
  decision_reason: string | null;
  created_at: string;
  decided_at: string | null;
}

const QUICK_AMOUNTS = [500_000, 1_000_000, 2_500_000, 5_000_000]; // minor units (TZS 5k…50k)
const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

function errText(err: unknown): string {
  if (err instanceof ApiError) return err.problem.detail ?? err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}

function stateBadge(state: string) {
  if (state === "approved") return "ev-badge ev-badge-mint";
  if (state === "rejected") return "ev-badge ev-badge-red";
  if (state === "cancelled") return "ev-badge";
  return "ev-badge ev-badge-amber";
}

/** Small preview of the dealer's own slip, fetched with the bearer token. */
function SlipPreview({ request }: { request: TopupRequest }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let created: string | null = null;
    setUrl(null);
    setError(null);
    fetchEvidenceUrl(`/superdealer/v1/topups/${request.id}/slip`)
      .then((next) => {
        created = next;
        setUrl(next);
      })
      .catch((err: unknown) => setError(errText(err)));
    return () => releaseEvidenceUrl(created);
  }, [request.id]);

  return (
    <div className="topup-preview">
      {error && <p className="ev-body-micro muted">{error}</p>}
      {!error && !url && <Loader2 size={20} className="spin" />}
      {url && request.file_mime === "application/pdf" && (
        <iframe className="topup-preview-frame" src={url} title={`Slip ${request.reference}`} />
      )}
      {url && request.file_mime !== "application/pdf" && (
        <img className="topup-preview-image" src={url} alt={`Slip ${request.reference}`} />
      )}
    </div>
  );
}

export function BankTopup() {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [amountText, setAmountText] = useState("");
  const [accountId, setAccountId] = useState("");
  const [payerName, setPayerName] = useState("");
  const [bankReference, setBankReference] = useState("");
  const [paidDate, setPaidDate] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<TopupRequest | null>(null);
  const [preview, setPreview] = useState<TopupRequest | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const accounts = useQuery({
    queryKey: ["dealer-topup-accounts"],
    queryFn: () => dealerApi.get<AccountsResponse>("/superdealer/v1/topups/bank-accounts"),
  });
  const history = useQuery({
    queryKey: ["dealer-topups"],
    queryFn: () =>
      dealerApi.get<{ data: TopupRequest[]; total: number }>("/superdealer/v1/topups?limit=20"),
  });

  const amountMinor = toMinor(amountText);
  const limits = accounts.data;
  const knownState = useMemo(
    () => new Map((history.data?.data ?? []).map((row) => [row.id, row.state])),
    [history.data],
  );

  const send = useMutation({
    mutationFn: () => {
      if (!file) throw new Error("Attach the bank slip first.");
      return submitSlip<{ request: TopupRequest; message: string }>(
        "/superdealer/v1/topups",
        {
          amount_minor: String(amountMinor ?? 0),
          account_id: accountId || undefined,
          payer_name: payerName || undefined,
          bank_reference: bankReference || undefined,
          paid_at: paidDate || undefined,
          note: note || undefined,
        },
        file,
      );
    },
    onSuccess: (result) => {
      setSubmitted(result.request);
      setFormError(null);
      setAmountText("");
      setBankReference("");
      setNote("");
      setFile(null);
      if (fileInput.current) fileInput.current.value = "";
      void queryClient.invalidateQueries({ queryKey: ["dealer-topups"] });
      void queryClient.invalidateQueries({ queryKey: ["dealer-wallet"] });
      void queryClient.invalidateQueries({ queryKey: ["dealer-dashboard"] });
    },
    onError: () => undefined,
  });

  const withdraw = useMutation({
    mutationFn: (id: string) => dealerApi.post(`/superdealer/v1/topups/${id}/cancel`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["dealer-topups"] });
      setPreview(null);
    },
  });

  const pickFile = (next: File | null | undefined) => {
    setFile(next ?? null);
    if (!next) return;
    if (!ACCEPTED.includes(next.type)) {
      setFormError("That file type is not accepted. Use a photo (JPG, PNG) or a PDF.");
      setFile(null);
      if (fileInput.current) fileInput.current.value = "";
      return;
    }
    if (limits && next.size > limits.slip_max_bytes) {
      setFormError(`The slip must be under ${Math.round(limits.slip_max_bytes / 1024 / 1024)} MB.`);
      setFile(null);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setSubmitted(null);
    if (!amountMinor || amountMinor <= 0) return setFormError("Enter the amount you transferred.");
    if (limits && amountMinor < limits.min_amount_minor)
      return setFormError(`The smallest top-up is ${tzs(limits.min_amount_minor)}.`);
    if (limits && amountMinor > limits.max_amount_minor)
      return setFormError(`One request cannot exceed ${tzs(limits.max_amount_minor)}.`);
    if (!file) return setFormError("Attach the bank slip as a photo or PDF.");
    send.mutate();
    return undefined;
  };

  const copyAccount = async (account: BankAccount) => {
    try {
      await navigator.clipboard.writeText(account.account_number ?? "");
      setCopied(account.id);
      window.setTimeout(() => setCopied(null), 1800);
    } catch {
      /* clipboard blocked — the number is on screen anyway */
    }
  };

  const rows = history.data?.data ?? [];

  return (
    <div>
      <PageHead
        title="Bank top-up"
        description="Pay into an SKT account at the bank, then send us the slip. We credit your float after checking it."
        actions={
          <span className="ev-badge ev-badge-blue">
            <Banknote size={12} /> {tzs(limits?.min_amount_minor ?? 5_000_000)} minimum
          </span>
        }
      />

      <div className="dealer-send-layout">
        <div className="admin-card">
          <div className="admin-card-header">
            <h3>Report a transfer</h3>
            <span className="ev-body-micro muted">Reviewed by SKT finance</span>
          </div>

          {submitted && (
            <div className="dealer-alert dealer-alert-ok">
              <CheckCircle2 size={16} />
              <span>
                Slip received. Reference <strong className="dash-mono">{submitted.reference}</strong>{" "}
                — quote it when you ask about this top-up.
              </span>
            </div>
          )}
          {send.isError && <div className="dealer-alert dealer-alert-error">{errText(send.error)}</div>}
          {formError && <div className="dealer-alert dealer-alert-error">{formError}</div>}

          <form onSubmit={submit}>
            <label className="admin-field">
              <span>Amount transferred (TZS)</span>
              <input
                className="ev-input"
                inputMode="decimal"
                placeholder="e.g. 1,000,000"
                value={amountText}
                onChange={(e) => setAmountText(e.target.value)}
                disabled={send.isPending}
              />
              <span className="ev-body-micro muted">
                {amountMinor ? `= ${tzsPlain(amountMinor)} TZS` : " "}
              </span>
            </label>

            <div className="dealer-chip-row">
              {QUICK_AMOUNTS.map((minor) => (
                <button
                  key={minor}
                  type="button"
                  className="dealer-chip"
                  onClick={() => setAmountText(String(minor / 100))}
                  disabled={send.isPending}
                >
                  {tzsPlain(minor)}
                </button>
              ))}
            </div>

            <label className="admin-field">
              <span>Account you paid into</span>
              <select
                className="ev-select"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                disabled={send.isPending || (limits?.accounts.length ?? 0) === 0}
              >
                <option value="">Select the account…</option>
                {(limits?.accounts ?? []).map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.bank} · {account.account_number}
                  </option>
                ))}
              </select>
              {(limits?.accounts.length ?? 0) === 0 && limits?.message && (
                <span className="ev-body-micro muted">{limits.message}</span>
              )}
            </label>

            <div className="topup-two-fields">
              <label className="admin-field">
                <span>Name on the account that paid</span>
                <input
                  className="ev-input"
                  value={payerName}
                  onChange={(e) => setPayerName(e.target.value)}
                  disabled={send.isPending}
                  placeholder="As printed on the slip"
                />
              </label>
              <label className="admin-field">
                <span>Bank reference</span>
                <input
                  className="ev-input"
                  value={bankReference}
                  onChange={(e) => setBankReference(e.target.value)}
                  disabled={send.isPending}
                  placeholder="Transaction code"
                />
              </label>
            </div>

            <label className="admin-field">
              <span>Date you paid</span>
              <input
                className="ev-input"
                type="date"
                value={paidDate}
                onChange={(e) => setPaidDate(e.target.value)}
                disabled={send.isPending}
              />
            </label>

            <label className="admin-field">
              <span>Note for SKT (optional)</span>
              <textarea
                className="ev-textarea"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={send.isPending}
                placeholder="Anything that helps us match it faster"
                style={{ resize: "vertical" }}
              />
            </label>

            <div
              className={`topup-dropzone${dragging ? " topup-dropzone-active" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                pickFile(e.dataTransfer.files?.[0]);
              }}
              role="button"
              tabIndex={0}
              onClick={() => fileInput.current?.click()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") fileInput.current?.click();
              }}
            >
              <input
                ref={fileInput}
                type="file"
                accept={ACCEPTED.join(",")}
                hidden
                onChange={(e) => pickFile(e.target.files?.[0])}
              />
              {file ? (
                <>
                  <FileText size={20} />
                  <div className="topup-dropzone-text">
                    <strong>{file.name}</strong>
                    <span className="ev-body-micro muted">
                      {(file.size / 1024 / 1024).toFixed(2)} MB ·{" "}
                      {file.type.replace("image/", "photo · ")}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="ev-icon-btn"
                    aria-label="Remove the slip"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      if (fileInput.current) fileInput.current.value = "";
                    }}
                  >
                    <X size={16} />
                  </button>
                </>
              ) : (
                <>
                  <ImageUp size={20} />
                  <div className="topup-dropzone-text">
                    <strong>Attach the bank slip</strong>
                    <span className="ev-body-micro muted">
                      Drag it here or tap to choose — photo (JPG, PNG) or PDF
                    </span>
                  </div>
                </>
              )}
            </div>

            <button
              type="submit"
              className="ev-btn ev-btn-primary ev-btn-lg"
              style={{ marginTop: 16, width: "100%" }}
              disabled={send.isPending}
            >
              {send.isPending ? <Loader2 size={18} className="spin" /> : <Banknote size={18} />}
              {send.isPending ? "Sending…" : "Send slip for approval"}
            </button>
          </form>
        </div>

        <div className="admin-card">
          <div className="admin-card-header">
            <h3>
              <Landmark size={16} /> Where to pay
            </h3>
            <span className="ev-body-micro muted">Given to you personally by SKT</span>
          </div>

          {(limits?.accounts ?? []).length === 0 && (
            <p className="ev-body muted">
              {limits?.message ?? "Loading collection accounts…"}
            </p>
          )}

          {(limits?.accounts ?? []).map((account) => (
            <div key={account.id} className="topup-account">
              <div className="topup-account-head">
                <strong>{account.bank}</strong>
                <button
                  type="button"
                  className="ev-btn ev-btn-sm ev-btn-ghost"
                  onClick={() => copyAccount(account)}
                >
                  <Copy size={14} /> {copied === account.id ? "Copied" : "Copy"}
                </button>
              </div>
              <dl className="topup-account-list">
                <div>
                  <dt>Account name</dt>
                  <dd>{account.account_name ?? "—"}</dd>
                </div>
                <div>
                  <dt>Account number</dt>
                  <dd className="dash-mono">{account.account_number ?? "—"}</dd>
                </div>
                {account.branch && (
                  <div>
                    <dt>Branch</dt>
                    <dd>{account.branch}</dd>
                  </div>
                )}
                {account.teller && <dd className="topup-account-note">{account.teller}</dd>}
              </dl>
              {account.note && <p className="ev-body-micro muted">{account.note}</p>}
            </div>
          ))}

          <ol className="topup-steps">
            <li>Transfer the float at the bank and keep the slip.</li>
            <li>Upload it here with the bank reference.</li>
            <li>SKT checks the statement, then your float appears in the wallet.</li>
            <li>
              Slips are deleted {limits?.retention_days ?? 45} days after a decision — the record
              stays.
            </li>
          </ol>
        </div>
      </div>

      <div className="admin-card" style={{ marginTop: 20, padding: 0, overflow: "hidden" }}>
        <div className="admin-card-header">
          <h3>My top-up requests</h3>
          <span className="ev-body-micro muted">{history.data?.total ?? 0} total</span>
        </div>
        {history.isLoading && (
          <div style={{ padding: 20 }}>
            <LoadingRow label="Loading your requests…" />
          </div>
        )}
        {history.data && rows.length === 0 && (
          <p className="ev-body muted" style={{ padding: 20 }}>
            No bank top-ups yet. The first one you send appears here with its status.
          </p>
        )}
        {rows.length > 0 && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th className="admin-table-th">Reference</th>
                  <th className="admin-table-th">Amount</th>
                  <th className="admin-table-th">Bank ref</th>
                  <th className="admin-table-th">Sent</th>
                  <th className="admin-table-th">Status</th>
                  <th className="admin-table-th" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="admin-table-row-hover">
                    <td className="admin-table-td" data-label="Reference">
                      <span className="ev-body-mono">{row.reference}</span>
                    </td>
                    <td className="admin-table-td" data-label="Amount">
                      <span className="ev-body-mono">{tzs(row.amount_minor)}</span>
                      {row.credited_amount_minor !== null &&
                        row.credited_amount_minor !== row.amount_minor && (
                          <div className="ev-body-micro muted">
                            credited {tzs(row.credited_amount_minor)}
                          </div>
                        )}
                    </td>
                    <td className="admin-table-td" data-label="Bank ref">
                      <span className="ev-body-micro">{row.bank_reference ?? "—"}</span>
                    </td>
                    <td className="admin-table-td" data-label="Sent">
                      <span className="ev-body-micro">{dateTime(row.created_at)}</span>
                    </td>
                    <td className="admin-table-td" data-label="Status">
                      <span className={stateBadge(row.state)}>{row.state}</span>
                    </td>
                    <td className="admin-table-td" data-label="">
                      <button
                        className="ev-btn ev-btn-sm ev-btn-ghost"
                        onClick={() => setPreview(row)}
                        disabled={knownState.get(row.id) === "cancelled"}
                      >
                        <Eye size={15} /> Slip
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {preview && (
        <div className="admin-drawer-overlay" onClick={() => setPreview(null)}>
          <div
            className="admin-drawer"
            onClick={(e) => e.stopPropagation()}
            style={{ width: 520, maxWidth: "100%" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <h3 style={{ marginBottom: 4 }}>{tzs(preview.amount_minor)}</h3>
                <span className="ev-body-micro muted">{preview.reference}</span>
              </div>
              <button className="ev-icon-btn" onClick={() => setPreview(null)} aria-label="Close">
                <X size={20} />
              </button>
            </div>

            <SlipPreview request={preview} />

            {preview.decision_reason && (
              <div className="dealer-alert dealer-alert-error" style={{ marginTop: 16 }}>
                <span>
                  <strong>{preview.state}:</strong> {preview.decision_reason}
                </span>
              </div>
            )}

            {preview.state === "pending" && (
              <button
                className="ev-btn ev-btn-outline"
                style={{ marginTop: 16 }}
                disabled={withdraw.isPending}
                onClick={() => withdraw.mutate(preview.id)}
              >
                {withdraw.isPending ? <Loader2 size={16} className="spin" /> : <X size={16} />}
                Withdraw this request
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function LoadingRow({ label }: { label: string }) {
  return (
    <span className="ev-body-micro muted" style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
      <Loader2 size={15} className="spin" /> {label}
    </span>
  );
}
